import {createContext, memo, use, useCallback, useMemo, useRef, useState} from 'react'

type ViewOptionsMenuControls = {
  toggle: () => void
  open: () => void
  close: () => void
}

type ViewOptionsMenuRefContextType = {
  anchorRef: React.RefObject<HTMLButtonElement | null>
  isMenuOpen: boolean
  registerMenuControls: (controls: ViewOptionsMenuControls | null) => void
  toggleMenu: () => void
  openMenu: () => void
  closeMenu: () => void
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const ViewOptionsMenuRefContext = createContext<ViewOptionsMenuRefContextType | null>(null)
const ViewOptionsMenuAnchorRefContext = createContext<React.RefObject<HTMLButtonElement | null> | null>(null)

export const ViewOptionsMenuRefContextProvider = memo(function ViewOptionsMenuRefContextProviderComponent({
  children,
}: {
  children: React.ReactNode
}) {
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const controlsRef = useRef<ViewOptionsMenuControls | null>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const registerMenuControls = useCallback((controls: ViewOptionsMenuControls | null) => {
    controlsRef.current = controls
  }, [])

  const toggleMenu = useCallback(() => {
    controlsRef.current?.toggle()
  }, [])

  const openMenu = useCallback(() => {
    controlsRef.current?.open()
  }, [])

  const closeMenu = useCallback(() => {
    controlsRef.current?.close()
  }, [])

  return (
    <ViewOptionsMenuRefContext
      value={useMemo<ViewOptionsMenuRefContextType>(
        () => ({
          anchorRef,
          isMenuOpen,
          registerMenuControls,
          toggleMenu,
          openMenu,
          closeMenu,
          setIsMenuOpen,
        }),
        [closeMenu, isMenuOpen, openMenu, registerMenuControls, toggleMenu],
      )}
    >
      <ViewOptionsMenuAnchorRefContext value={anchorRef}>{children}</ViewOptionsMenuAnchorRefContext>
    </ViewOptionsMenuRefContext>
  )
})

export const useViewOptionsMenuRef = () => {
  const context = use(ViewOptionsMenuRefContext)
  if (!context) {
    throw new Error('useViewOptionsMenu must be used within a ViewOptionsMenuRefContextProvider')
  }

  return context
}

export const useViewOptionsMenuAnchorRef = () => {
  const anchorRef = use(ViewOptionsMenuAnchorRefContext)
  if (!anchorRef) {
    throw new Error('useViewOptionsMenuAnchorRef must be used within a ViewOptionsMenuRefContextProvider')
  }

  return anchorRef
}
